import { Injectable, Injector } from "@angular/core";
import { environment } from "../../environments/environment";
import { AuthRedirectService } from "./auth-redirect.service";
import { usesFootballerAuth } from "./footballer-auth.service";

@Injectable({
  providedIn: "root"
})
export class AuthSessionRecoveryService {
  private redirectInProgress = false;

  constructor(private injector: Injector) {}

  public recoverFromStaleSession(error: any): boolean {
    if (usesFootballerAuth) {
      if (error?.status !== 401) return false;
      const target = this.currentTargetUrl();
      if (this.isPublicGuestPath(target) || this.extractPath(target) === '/auth') return true;
      if (this.redirectInProgress) return true;
      this.redirectInProgress = true;
      this.injector.get(AuthRedirectService).login(target).subscribe({
        complete: () => { this.redirectInProgress = false; },
        error: () => { this.redirectInProgress = false; }
      });
      return true;
    }
    if (!this.isStaleSessionError(error)) {
      return false;
    }

    if (this.isPublicGuestPath(this.currentTargetUrl())) {
      this.logDebug("Missing refresh token detected on public guest flow; clearing stale local Auth0 cache.", error);
      const clearedKeys = this.clearAuth0SpaCache();
      if (this.isPublicLandingPath(this.currentTargetUrl()) && clearedKeys > 0) {
        setTimeout(() => window.location.replace(window.location.origin + "/"), 0);
      }
      return true;
    }

    if (this.redirectInProgress) {
      return true;
    }

    this.redirectInProgress = true;
    this.logDebug("Missing refresh token detected; redirecting through Auth0 login.", error);
    this.clearAuth0SpaCache();
    this.injector.get(AuthRedirectService).login(this.currentTargetUrl()).subscribe(
      () => undefined,
      () => {
        this.redirectInProgress = false;
      }
    );

    return true;
  }

  private isStaleSessionError(error: any): boolean {
    const text = this.errorText(error);
    return text.indexOf("missing_refresh_token") >= 0 ||
      text.indexOf("missing refresh token") >= 0 ||
      text.indexOf("invalid refresh token") >= 0;
  }

  private errorText(error: any): string {
    const parts = [
      error && error.error,
      error && error.error_description,
      error && error.message,
      error ? String(error) : ""
    ];

    return parts
      .filter(value => value != null)
      .map(value => String(value).toLowerCase())
      .join(" ");
  }

  private currentTargetUrl(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  private isPublicGuestPath(targetUrl: string): boolean {
    const path = this.extractPath(targetUrl);
    return this.isPublicLandingPath(targetUrl) ||
      path.indexOf("/guest-manager") === 0 ||
      path.indexOf("/guest-upgrade") === 0 ||
      path.indexOf("/profile/") === 0 ||
      path === "/email-verified" ||
      path.indexOf("/join/") === 0 ||
      path.indexOf("/public-event-invite/") === 0 ||
      path.indexOf("/i/") === 0 ||
      path.indexOf("/claim/") === 0;
  }

  private isPublicLandingPath(targetUrl: string): boolean {
    const path = this.extractPath(targetUrl);
    return path === "" || path === "/";
  }

  private extractPath(targetUrl: string): string {
    if (!targetUrl) {
      return "";
    }

    const hashIndex = targetUrl.indexOf("#/");
    if (hashIndex >= 0) {
      return targetUrl.substring(hashIndex + 1).split("?")[0];
    }

    return targetUrl.split("?")[0];
  }

  private logDebug(message: string, error: any): void {
    if (environment.production && localStorage.getItem(environment.DEBUG) !== "true") {
      return;
    }

    console.warn("[Auth0 session recovery]", message, {
      origin: window.location.origin,
      targetUrl: this.currentTargetUrl(),
      error: this.safeError(error),
      auth0SpaCacheKeys: this.auth0SpaCacheKeyCount()
    });
  }

  private safeError(error: any): object {
    return {
      error: error && error.error,
      errorDescription: error && error.error_description,
      message: error && error.message
    };
  }

  private auth0SpaCacheKeyCount(): number {
    try {
      return Object.keys(localStorage)
        .filter((key) => key.indexOf("@@auth0spajs@@") === 0)
        .length;
    } catch (_) {
      return -1;
    }
  }

  private clearAuth0SpaCache(): number {
    try {
      const keys = Object.keys(localStorage)
        .filter((key) => key.indexOf("@@auth0spajs@@") === 0);
      keys.forEach((key) => localStorage.removeItem(key));
      return keys.length;
    } catch (_) {
      return 0;
    }
  }
}
